import React from "react";
import { Clock, ChevronDown, ChevronUp, Loader2, BellOff } from "lucide-react";
import { toast } from "sonner";
import {
  DURATION_PRESETS,
  LEAD_OPTIONS,
  DEFAULT_LEAD,
  readTimer,
  secondsLeft,
  formatLeft,
  formatEndsAt,
  toLocalInput,
  startTimer,
  stopTimer,
  syncTimer,
} from "../lib/parkingTimer";
import { pushSupported, permissionState, ensurePushSubscription, sendTestPush } from "../lib/push";
import { track } from "../lib/analytics";

const UNITS = { minutes: 1, hours: 60, days: 1440 };

/** Parking clock, lives inside the My Car sheet. Collapsed by default. */
export const ParkingClock = ({ open }) => {
  const [timer, setTimer] = React.useState(() => readTimer());
  const [expanded, setExpanded] = React.useState(false);
  const [mode, setMode] = React.useState("preset"); // preset | custom | until
  const [customValue, setCustomValue] = React.useState("45");
  const [customUnit, setCustomUnit] = React.useState("minutes");
  const [until, setUntil] = React.useState(() => toLocalInput(new Date(Date.now() + 3600000)));
  const [lead, setLead] = React.useState(timer?.lead_minutes ?? DEFAULT_LEAD);
  const [busy, setBusy] = React.useState(false);
  const [pushReady, setPushReady] = React.useState(null);
  const [testing, setTesting] = React.useState(false);
  const [, tick] = React.useReducer((n) => n + 1, 0);

  React.useEffect(() => {
    if (!open) return;
    syncTimer().then((t) => {
      setTimer(t);
      if (t?.lead_minutes) setLead(t.lead_minutes);
    });
  }, [open]);

  // One interval drives the countdown; hiding the panel never stops it.
  React.useEffect(() => {
    if (!timer) return undefined;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timer]);

  const left = secondsLeft(timer);
  const expired = left !== null && left <= 0;

  const endsAtFor = () => {
    if (mode === "until") {
      const d = new Date(until);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (mode === "custom") {
      const n = Number(customValue);
      if (!Number.isFinite(n) || n <= 0) return null;
      return new Date(Date.now() + n * UNITS[customUnit] * 60000);
    }
    return null;
  };

  const begin = async (endsAt) => {
    if (!endsAt || endsAt.getTime() <= Date.now()) {
      toast.error("Choose a time in the future.");
      return;
    }
    setBusy(true);
    try {
      // Permission is only requested once the driver actually wants reminders,
      // and a subscription is (re)registered even when permission was granted
      // earlier — otherwise the server has nowhere to send the reminder.
      const ensured = await ensurePushSubscription();
      setPushReady(ensured.ok);
      if (!ensured.ok) {
        toast.message("Reminders will only show while SpotDrop is open on this device.");
      }
      const t = await startTimer({ endsAt, leadMinutes: lead });
      setTimer(t);
      setExpanded(false);
      track("parking_timer_started", { lead, minutes: Math.round((endsAt - Date.now()) / 60000) });
      toast.success(`Parking clock set — ends ${formatEndsAt(t)}`);
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    setBusy(true);
    try {
      await stopTimer();
      setTimer(null);
      track("parking_timer_cleared", {});
    } finally {
      setBusy(false);
    }
  };

  const noPush = !pushSupported() || permissionState() === "denied" || pushReady === false;

  const runTest = async () => {
    setTesting(true);
    try {
      const res = await sendTestPush();
      setPushReady(res.ok);
      if (res.ok) toast.success("Test notification sent — check your notification shade.");
      else if (res.reason === "denied") toast.error("Notifications are blocked for SpotDrop in your browser settings.");
      else if (res.reason === "unsupported") toast.error("This browser cannot deliver background notifications.");
      else toast.error("Could not deliver a notification to this device.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div data-testid="parking-clock">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-3 py-3 flex items-center gap-2.5 text-left active:bg-white/5"
        data-testid="parking-clock-toggle"
        aria-expanded={expanded}
      >
        <Clock className={`w-4 h-4 shrink-0 ${expired ? "text-red-400" : timer ? "text-[#ccff00]" : "text-zinc-400"}`} />
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] text-white">Parking clock</span>
          {timer ? (
            <span
              className={`block text-[11px] ${expired ? "text-red-400" : "text-zinc-400"}`}
              data-testid="parking-clock-remaining"
            >
              {expired ? `Time up ${formatLeft(left)}` : `${formatLeft(left)} left · ends ${formatEndsAt(timer)}`}
            </span>
          ) : (
            <span className="block text-[11px] text-zinc-500">Set a reminder before your time runs out</span>
          )}
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-zinc-500 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3" data-testid="parking-clock-panel">
          {timer ? (
            <div className="rounded-xl border border-white/10 p-3">
              <div className="text-[11px] text-zinc-500">Ends at</div>
              <div className="text-[15px] text-white font-semibold" data-testid="parking-clock-ends-at">
                {formatEndsAt(timer)}
              </div>
              <div className="mt-1 text-[12px] text-zinc-400">
                Reminder {timer.lead_minutes} min before, plus one when the time is up.
              </div>
              <button
                type="button"
                onClick={end}
                disabled={busy}
                className="mt-3 w-full h-10 rounded-xl border border-white/15 text-[13px] text-zinc-200 active:scale-[0.99] transition-transform disabled:opacity-60"
                data-testid="parking-clock-stop"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Clear parking clock"}
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5" data-testid="parking-clock-presets">
                {DURATION_PRESETS.map((p) => (
                  <button
                    key={p.minutes}
                    type="button"
                    disabled={busy}
                    onClick={() => { setMode("preset"); begin(new Date(Date.now() + p.minutes * 60000)); }}
                    className="h-9 px-3 rounded-full border border-white/15 text-[12px] text-zinc-200 active:scale-95 transition-transform disabled:opacity-60"
                    data-testid={`parking-clock-preset-${p.minutes}`}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setMode(mode === "custom" ? "preset" : "custom")}
                  className={`h-9 px-3 rounded-full border text-[12px] active:scale-95 transition-transform ${
                    mode === "custom" ? "border-[#ccff00]/50 text-[#ccff00]" : "border-white/15 text-zinc-200"
                  }`}
                  data-testid="parking-clock-custom-btn"
                >
                  Custom
                </button>
                <button
                  type="button"
                  onClick={() => setMode(mode === "until" ? "preset" : "until")}
                  className={`h-9 px-3 rounded-full border text-[12px] active:scale-95 transition-transform ${
                    mode === "until" ? "border-[#ccff00]/50 text-[#ccff00]" : "border-white/15 text-zinc-200"
                  }`}
                  data-testid="parking-clock-until-btn"
                >
                  Until date/time
                </button>
              </div>

              {mode === "custom" && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    className="w-20 h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-[13px] text-white"
                    data-testid="parking-clock-custom-value"
                  />
                  <select
                    value={customUnit}
                    onChange={(e) => setCustomUnit(e.target.value)}
                    className="h-10 px-2 rounded-xl bg-white/5 border border-white/10 text-[13px] text-white"
                    data-testid="parking-clock-custom-unit"
                  >
                    <option value="minutes">minutes</option>
                    <option value="hours">hours</option>
                    <option value="days">days</option>
                  </select>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => begin(endsAtFor())}
                    className="flex-1 h-10 rounded-xl bg-[#ccff00] text-[#0a0a0c] font-semibold text-[13px] active:scale-[0.99] transition-transform disabled:opacity-60"
                    data-testid="parking-clock-custom-start"
                  >
                    Start
                  </button>
                </div>
              )}

              {mode === "until" && (
                <div className="flex items-center gap-2">
                  <input
                    type="datetime-local"
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                    className="flex-1 h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-[13px] text-white"
                    data-testid="parking-clock-until-input"
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => begin(endsAtFor())}
                    className="h-10 px-4 rounded-xl bg-[#ccff00] text-[#0a0a0c] font-semibold text-[13px] active:scale-[0.99] transition-transform disabled:opacity-60"
                    data-testid="parking-clock-until-start"
                  >
                    Start
                  </button>
                </div>
              )}

              <div>
                <div className="text-[11px] text-zinc-500 mb-1.5">Remind me before the end</div>
                <div className="flex gap-1.5" data-testid="parking-clock-leads">
                  {LEAD_OPTIONS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setLead(m)}
                      className={`h-8 px-3 rounded-full border text-[12px] active:scale-95 transition-transform ${
                        lead === m ? "border-[#ccff00]/50 text-[#ccff00] bg-[#ccff00]/10" : "border-white/15 text-zinc-300"
                      }`}
                      data-testid={`parking-clock-lead-${m}`}
                    >
                      {m} min
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={runTest}
            disabled={testing}
            className="w-full h-9 rounded-xl border border-white/15 text-[12px] text-zinc-300 active:scale-[0.99] transition-transform disabled:opacity-60"
            data-testid="parking-clock-test-push"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Send test notification"}
          </button>

          {pushReady === true && (
            <p className="text-[11px] text-[#ccff00]/80" data-testid="parking-clock-push-ok">
              Background reminders are armed on this device.
            </p>
          )}

          {noPush && (
            <p className="text-[11px] text-amber-300/90 flex items-start gap-2" data-testid="parking-clock-no-push">
              <BellOff className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Notifications are blocked on this device, so the countdown only works while SpotDrop
              is open. Allow notifications in your browser settings to be reminded in the
              background.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ParkingClock;
